import type * as React from 'react';
import { createContext, type FC, useContext, useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';

import { SECTIONS } from '../constants';

interface ScrollContextType {
  sectionsAvailable: string[];
  sectionVisible: string;
  scrollToSection: (newSection: string) => void;
  scrollToNextSection: () => void;
  handleSetSectionVisible: (sectionName: string) => void;
}

export const Context = createContext<ScrollContextType>({
  sectionsAvailable: SECTIONS,
  sectionVisible: '',
  // biome-ignore lint/suspicious/noEmptyBlockStatements: legacy code - suspicious/noEmptyBlockStatements
  scrollToSection: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: legacy code - suspicious/noEmptyBlockStatements
  scrollToNextSection: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: legacy code - suspicious/noEmptyBlockStatements
  handleSetSectionVisible: () => {},
});

export const ScrollContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [sectionVisible, setSectionVisible] = useState<string>('');
  const { logEvent } = useAmplitude();

  const scrollToSection = (newSection: string) => {
    const DOMElement = document.getElementById(newSection);
    const offsetTop = DOMElement?.offsetTop;

    if (offsetTop) {
      window.scroll({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
    logEvent({
      event: 'Click Button Slide Down',
      data: {
        ...getSourceEventParams(),
      },
    });
    setSectionVisible(newSection);
  };

  const handleSetSectionVisible = (sectionName: string) => {
    setSectionVisible(sectionName);
  };

  const scrollToNextSection = () => {
    const currentIndex = SECTIONS.indexOf(sectionVisible);

    if (currentIndex <= SECTIONS.length) {
      scrollToSection(SECTIONS[currentIndex + 1]);
    }
  };

  return (
    <Context.Provider
      value={{
        sectionVisible,
        scrollToSection,
        scrollToNextSection,
        sectionsAvailable: SECTIONS,
        handleSetSectionVisible,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useScrollContext = () => {
  const context = useContext(Context);

  return context;
};
